import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coffee, Maximize2, Minimize2, Pause, Play, Plus, RotateCcw } from "lucide-react";
import PublicLayout from "@/components/site/PublicLayout";
import SplitFlap from "@/components/site/SplitFlap";
import { isAuthenticated } from "@/lib/api";

// The free, no-account focus timer. Everything stays in this browser: sessions are never sent to
// the API, and the only thing kept is today's tally in localStorage.
const PRESETS = [25, 45, 60, 90];
const BREAK_MINUTES = 5;
const TALLY_KEY = "hm_public_focus_tally";
const MINUTE = 60000;

const today = () => new Date().toDateString();
function readTally() {
  try {
    const saved = JSON.parse(localStorage.getItem(TALLY_KEY) || "null");
    if (saved?.day === today()) return saved;
  } catch { /* unreadable or blocked storage starts a fresh tally */ }
  return { day: today(), sessions: 0, minutes: 0 };
}
function saveTally(tally) {
  try { localStorage.setItem(TALLY_KEY, JSON.stringify(tally)); } catch { /* the tally simply isn't remembered */ }
}

function chime() {
  try {
    const Context = window.AudioContext || window.webkitAudioContext;
    const audio = new Context();
    [660, 880].forEach((frequency, index) => {
      const tone = audio.createOscillator();
      const gain = audio.createGain();
      tone.frequency.value = frequency;
      tone.connect(gain);
      gain.connect(audio.destination);
      const at = audio.currentTime + index * 0.22;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.18, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.6);
      tone.start(at);
      tone.stop(at + 0.65);
    });
  } catch { /* no audio: the finished state is still shown */ }
}

const clock = (ms) => {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  return `${String(minutes).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

export default function PublicFocus() {
  const [mode, setMode] = useState("focus");
  const [minutes, setMinutes] = useState(25);
  const [custom, setCustom] = useState(30);
  const [status, setStatus] = useState("idle"); // idle | running | paused | done
  const [endsAt, setEndsAt] = useState(null);
  const [remaining, setRemaining] = useState(25 * MINUTE);
  const [planned, setPlanned] = useState(25 * MINUTE);
  const [now, setNow] = useState(Date.now());
  const [task, setTask] = useState("");
  const [tally, setTally] = useState(readTally);
  const [fullscreen, setFullscreen] = useState(false);
  const roomRef = useRef(null);
  const signedIn = isAuthenticated();

  const left = status === "running" ? Math.max(0, endsAt - now) : remaining;

  // `minutes` is the chosen focus length; a break never overwrites it.
  const setLength = (value, nextMode = mode) => {
    setMode(nextMode);
    if (nextMode === "focus") setMinutes(value);
    setPlanned(value * MINUTE);
    setRemaining(value * MINUTE);
    setStatus("idle");
    setEndsAt(null);
  };

  const start = useCallback(() => {
    setEndsAt(Date.now() + remaining);
    setNow(Date.now());
    setStatus("running");
  }, [remaining]);

  const pause = useCallback(() => {
    setRemaining(Math.max(0, endsAt - Date.now()));
    setStatus("paused");
  }, [endsAt]);

  const reset = () => setLength(mode === "focus" ? minutes : BREAK_MINUTES);

  const addFive = () => {
    setPlanned((value) => value + 5 * MINUTE);
    if (status === "running") setEndsAt((value) => value + 5 * MINUTE);
    else setRemaining((value) => value + 5 * MINUTE);
  };

  useEffect(() => {
    if (status !== "running") return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [status]);

  useEffect(() => {
    if (status !== "running" || left > 0) return;
    setStatus("done");
    setRemaining(0);
    chime();
    if (mode === "focus") {
      const next = { day: today(), sessions: (tally.day === today() ? tally.sessions : 0) + 1, minutes: (tally.day === today() ? tally.minutes : 0) + Math.round(planned / MINUTE) };
      setTally(next);
      saveTally(next);
    }
  }, [status, left, mode, planned, tally]);

  useEffect(() => {
    const original = document.title;
    if (status === "running" || status === "paused") document.title = `${clock(left)} · ${mode === "focus" ? "Focus" : "Break"} — HustlrMate`;
    else if (status === "done") document.title = mode === "focus" ? "Done · Focus — HustlrMate" : "Break over — HustlrMate";
    return () => { document.title = original; };
  }, [status, left, mode]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.code !== "Space" || ["INPUT", "TEXTAREA", "BUTTON"].includes(event.target.tagName)) return;
      event.preventDefault();
      if (status === "running") pause();
      else if (status !== "done") start();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status, start, pause]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === roomRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await roomRef.current?.requestFullscreen();
    } catch { /* fullscreen unavailable */ }
  };

  const applyCustom = () => {
    const value = Number(custom);
    if (Number.isInteger(value) && value >= 1 && value <= 480) setLength(value, "focus");
  };

  const progress = planned ? Math.min(100, ((planned - left) / planned) * 100) : 0;
  const stateLabel = status === "running" ? (mode === "focus" ? "In session" : "On a break") : status === "paused" ? "Paused" : status === "done" ? (mode === "focus" ? "Session complete" : "Break over") : "Ready";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-6 sm:px-10">
        <div className="mb-8 max-w-2xl">
          <div className="overline mb-3">Free focus timer · no account needed</div>
          <h1 className="font-serif-editorial text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">One task. One focused block.</h1>
          <p className="mt-3 text-sm text-[#A1A1AA]">Pick a length, name the one thing you'll work on, and press start (or the space bar). Nothing here is uploaded; it all stays in this browser.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <div ref={roomRef} className="focus-room-card hm-card relative overflow-auto rounded-md p-6 text-center sm:p-10" data-testid="public-focus">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#F59E0B]/[0.03] to-transparent" />
            <button onClick={toggleFullscreen} className="absolute right-4 top-4 z-10 rounded-md border border-white/10 p-2 text-[#A1A1AA] transition hover:border-white/30 hover:text-white" aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <div className="overline mb-6">{mode === "focus" ? "Focus" : "Break"} · {Math.round(planned / MINUTE)} min</div>

            <div className="mx-auto mb-6 max-w-md">
              {status === "idle" && mode === "focus" ? (
                <input value={task} maxLength={120} onChange={(event) => setTask(event.target.value)} placeholder="What's the one thing? (optional)" aria-label="What you're working on" className="hm-input w-full rounded-md px-4 py-2.5 text-center text-sm" />
              ) : task && mode === "focus" ? (
                <div className="text-sm text-[#F5F5F0]">{task}</div>
              ) : null}
            </div>

            <div className="relative">
              <SplitFlap value={clock(left)} label={`${clock(left)} remaining`} className="mx-auto" />
              <div className="mx-auto mt-5 h-0.5 max-w-sm overflow-hidden rounded-full bg-white/[0.06]" aria-hidden="true"><div className="h-full bg-[#F59E0B] transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-[#A1A1AA]" aria-live="polite">{stateLabel}</div>
            </div>

            {status === "idle" && (
              <>
                <div className="mt-8 flex flex-wrap justify-center gap-2">
                  {PRESETS.map((value) => (
                    <button key={value} onClick={() => setLength(value, "focus")} className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${mode === "focus" && minutes === value ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA] hover:text-[#F5F5F0]"}`}>{value} min</button>
                  ))}
                </div>
                <div className="mx-auto mt-3 flex max-w-xs items-end justify-center gap-2">
                  <label className="text-left"><span className="text-[9px] uppercase tracking-[0.16em] text-[#A1A1AA]">Custom minutes</span><input type="number" min="1" max="480" value={custom} onChange={(event) => setCustom(event.target.value)} className="hm-input mt-1 w-28 rounded-md px-3 py-2 text-sm" /></label>
                  <button onClick={applyCustom} className="hm-btn-ghost rounded-md px-3 py-2 text-xs">Set</button>
                </div>
              </>
            )}

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {status === "done" ? (
                mode === "focus" ? (
                  <>
                    <button onClick={() => { setLength(BREAK_MINUTES, "break"); setEndsAt(Date.now() + BREAK_MINUTES * MINUTE); setNow(Date.now()); setStatus("running"); }} className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm"><Coffee className="h-4 w-4" /> {BREAK_MINUTES}-minute break</button>
                    <button onClick={() => setLength(minutes, "focus")} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold"><Play className="h-4 w-4" fill="currentColor" /> Another block</button>
                  </>
                ) : (
                  <button onClick={() => setLength(minutes, "focus")} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold"><Play className="h-4 w-4" fill="currentColor" /> Back to focus</button>
                )
              ) : (
                <>
                  {status === "running" ? (
                    <button onClick={pause} className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold"><Pause className="h-4 w-4" /> Pause</button>
                  ) : (
                    <button onClick={start} data-testid="public-focus-start" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold"><Play className="h-4 w-4" fill="currentColor" /> {status === "paused" ? "Resume" : "Start"}</button>
                  )}
                  {status !== "idle" && <button onClick={addFive} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-4 py-3 text-sm"><Plus className="h-4 w-4" /> 5 min</button>}
                  {status !== "idle" && <button onClick={reset} className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-4 py-3 text-sm" aria-label="Reset timer"><RotateCcw className="h-4 w-4" /></button>}
                </>
              )}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="hm-card rounded-md p-5">
              <div className="overline mb-3">Today, in this browser</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md border border-white/5 p-3"><div className="font-serif-editorial text-2xl lining-nums">{tally.sessions}</div><div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">Sessions</div></div>
                <div className="rounded-md border border-white/5 p-3"><div className="font-serif-editorial text-2xl lining-nums">{Math.round((tally.minutes / 60) * 10) / 10}h</div><div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">Focused</div></div>
              </div>
              <p className="mt-3 text-xs text-[#7c7c84]">Not saved anywhere else. Clearing your browser data clears it.</p>
            </div>

            <div className={`hm-card rounded-md p-5 ${status === "done" && mode === "focus" ? "ring-1 ring-[#F59E0B]/50" : ""}`}>
              <div className="overline mb-2">{status === "done" && mode === "focus" ? "Block done" : "Focus is easier witnessed"}</div>
              <p className="text-sm leading-relaxed text-[#D4D4D8]">
                {status === "done" && mode === "focus"
                  ? "Nobody saw that but you. On HustlrMate a partner sees your sessions, your streak and the days you skip."
                  : "Alone, a timer is easy to ignore. With a partner, someone notices when you don't show up."}
              </p>
              {signedIn ? (
                <Link to="/tools/focus" className="hm-btn-primary mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-xs font-semibold">Open your focus room <ArrowRight className="h-3.5 w-3.5" /></Link>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/cohorts" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-xs font-semibold">Join a cohort <ArrowRight className="h-3.5 w-3.5" /></Link>
                  <Link to="/auth" className="hm-btn-ghost rounded-md px-4 py-2.5 text-xs">Find a partner</Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
}
